import type { BlockContent } from 'components/editor/grid/reduxStore/editorSlice.ts';
import { clearTemporaryDimensionsForBlock } from 'components/editor/grid/reduxStore/editorSlice.ts';
import { addTemporaryDimensions } from 'components/editor/grid/reduxStore/editorSlice.ts';
import { updateGridDimensionConfig } from 'components/editor/grid/reduxStore/editorSlice.ts';
import { getRoundedValue } from 'components/editor/GridDndEditor/gridHelper.tsx';
import { ALLOWED_DECIMAL_PLACES_FOR_DIMENSION } from 'components/editor/hooks/UseBlockDimensionChangedHandler.ts';
import type { DimensionType } from './useAspectRatio.ts';
import { DIMENSION_WIDTH } from './useAspectRatio.ts';
import { type RootState, useAppDispatch } from 'components/editor/grid/reduxStore/Store.ts';
import { useNewBlockDimensionChangedHandler } from 'components/editor/hooks/useNewBlockDimensionChangedHandler.ts';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

export function useNewAspectRatio({
  imageAspectRatioLock,
  sectionId,
  blockId,
}: {
  imageAspectRatioLock: boolean;
  sectionId: string | null;
  blockId: string;
}) {
  const blockNewDimensionChangedHandler = useNewBlockDimensionChangedHandler();
  const dispatch = useAppDispatch();
  const editorContent = useSelector((state: RootState) => state.gridBlockReducer);

  const latestEditorStateRef = useRef(editorContent);
  const latestDimensionHandlerRef = useRef(blockNewDimensionChangedHandler);

  const handleImageDimensionChange = (
    changedDimension: DimensionType,
    updatedDimensionValue: number,
    commitToHistory = true,
    originalDimensions: { width: number; height: number; blockId: string; blockContent: BlockContent }
  ) => {
    if (!originalDimensions) {
      return;
    }

    const { blockId, blockContent, width: originalWidth, height: originalHeight } = originalDimensions;

    if (!sectionId || !blockId || !blockContent) {
      return;
    }

    let newWidth = originalWidth;
    let newHeight = originalHeight;
    const isWidthChanged = changedDimension === DIMENSION_WIDTH;
    const aspectRatio = originalWidth / originalHeight;

    if (imageAspectRatioLock && aspectRatio && !isNaN(aspectRatio)) {
      if (isWidthChanged) {
        newWidth = updatedDimensionValue;
        newHeight = updatedDimensionValue / aspectRatio;
      } else {
        newHeight = updatedDimensionValue;
        newWidth = updatedDimensionValue * aspectRatio;
      }
    } else {
      if (isWidthChanged) {
        newWidth = updatedDimensionValue;
      } else {
        newHeight = updatedDimensionValue;
      }
    }

    newWidth = getRoundedValue(newWidth, ALLOWED_DECIMAL_PLACES_FOR_DIMENSION);
    newHeight = getRoundedValue(newHeight, ALLOWED_DECIMAL_PLACES_FOR_DIMENSION);

    const stateBeforeResizing = {
      widthPx: originalWidth,
      heightPx: originalHeight,
      xAxisPx: blockContent.blockConfig.x,
      yAxisPx: blockContent.blockConfig.y,
    };

    const stateAfterResizing = {
      widthPx: newWidth,
      heightPx: newHeight,
      xAxisPx: blockContent.blockConfig.x,
      yAxisPx: blockContent.blockConfig.y,
    };
    const isDimensionUpdated =
      stateAfterResizing.widthPx !== stateBeforeResizing.widthPx || stateAfterResizing.heightPx !== stateBeforeResizing.heightPx;

    if (commitToHistory && isDimensionUpdated) {
      blockNewDimensionChangedHandler(sectionId, blockId, stateBeforeResizing, stateAfterResizing);
      dispatch(clearTemporaryDimensionsForBlock({ blockId }));
    } else {
      dispatch(
        updateGridDimensionConfig({
          sectionId,
          blockId,
          width: stateAfterResizing.widthPx,
          height: stateAfterResizing.heightPx,
        })
      );
      dispatch(
        addTemporaryDimensions({
          sectionId,
          blockId,
          originalDimensions: blockContent.blockConfig,
        })
      );
    }
  };

  useEffect(() => {
    latestEditorStateRef.current = editorContent;
  }, [editorContent]);

  useEffect(() => {
    latestDimensionHandlerRef.current = blockNewDimensionChangedHandler;
  }, [blockNewDimensionChangedHandler]);

  useEffect(() => {
    return () => {
      const tempDim = latestEditorStateRef.current.temporaryDimensions[blockId];
      if (tempDim) {
        const currentBlockContent = latestEditorStateRef.current.sections.find((s) => s.id === tempDim.sectionId)?.blocksContent[blockId];

        if (currentBlockContent) {
          const stateBeforeResizing = {
            widthPx: tempDim.originalDimensions.width,
            heightPx: tempDim.originalDimensions.height,
            xAxisPx: tempDim.originalDimensions.x,
            yAxisPx: tempDim.originalDimensions.y,
          };
          const stateAfterResizing = {
            widthPx: currentBlockContent.blockConfig.width,
            heightPx: currentBlockContent.blockConfig.height,
            xAxisPx: currentBlockContent.blockConfig.x,
            yAxisPx: currentBlockContent.blockConfig.y,
          };

          latestDimensionHandlerRef.current(tempDim.sectionId, blockId, stateBeforeResizing, stateAfterResizing);
        }
        dispatch(clearTemporaryDimensionsForBlock({ blockId }));
      }
    };
  }, [blockId, dispatch]);

  return { handleImageDimensionChange };
}
